import {
  Alert,
  Autocomplete,
  AutocompleteItem,
  DatePicker,
  Image,
  Textarea,
} from '@heroui/react';
import CustomButton from '../../components/CustomButton';
import CustomInput from '../../components/CustomInput';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { ROLE } from '../../../Role';
import ProfileImg from '/fallback-profile-1.avif?url';
import { Seo } from '../../../core/seo';

const ProviderRegistration = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const auth = useAuth();

  useEffect(() => {
    if (auth.isLoading) return;

    const roles = auth.user?.profile['cognito:groups'] as string[] | undefined;
    const hasProviderRole = roles?.includes(ROLE.PROVIDER);
    const hasCustomerRole = roles?.includes(ROLE.CUSTOMER);

    const onlyCustomer = hasCustomerRole && !hasProviderRole;

    if (!onlyCustomer) {
      navigate('/home', { replace: true });
    }
  }, [navigate, auth.isLoading, auth.user]);

  const gender = [
    { label: t('provider_registration.male'), key: 'male' },
    { label: t('provider_registration.female'), key: 'female' },
    { label: t('provider_registration.other'), key: 'other' },
  ];

  const language = [
    { label: t('provider_registration.english'), key: 'en' },
    { label: t('provider_registration.french'), key: 'fr' },
  ];

  const currency = [
    { label: 'USD', key: 'usd' },
    { label: 'CAD', key: 'cad' },
  ];

  const title = t('provider_registration.verify_title');
  const description = t('provider_registration.verify_description');

  return (
    <>
      <Seo
        title={t('provider_registration.seo_title')}
        description={t('provider_registration.seo_description')}
        canonicalPath="/registration-provider"
      />
      <div>
        <div className="mx-20 mb-20 mt-10">
          <h3 className="text-2xl font-semibold text-gray-700 mb-5">
            {t('provider_registration.create_account')}
          </h3>
          <div className="flex items-center justify-center w-full mb-5">
            <Alert
              variant="faded"
              color="primary"
              description={description}
              title={title}
            />
          </div>
          <div className="flex justify-start items-center mb-6">
            <div className="flex flex-initial items-center gap-4"></div>
            <Image
              className="w-28 h-28 rounded-full object-contain mx-auto"
              src={ProfileImg}
              alt={t('provider_registration.profile_alt')}
            />
            <div className="flex flex-col gap-2 ml-4">
              <div>
                <CustomButton
                  label={t('provider_registration.upload_profile')}
                  color="primary"
                />
              </div>
              <small className="text-xs text-gray-500">
                {t('provider_registration.image_note')}
              </small>
            </div>
          </div>

          {/* GENERAL INFORMATION */}
          <div className="mt-10">
            <h3 className="text-lg font-semibold text-gray-700 mb-7">
              {t('provider_registration.general_information')}
            </h3>
            <div className="grid grid-cols-4 gap-5 mt-2">
              <div>
                <CustomInput
                  label={t('provider_registration.your_name')}
                  isRequired
                  radius="sm"
                  variant="bordered"
                  size="md"
                  placeholder={t('provider_registration.your_name_placeholder')}
                  type="text"
                />
              </div>

              <div>
                <CustomInput
                  isRequired
                  radius="sm"
                  variant="bordered"
                  size="md"
                  label={t('provider_registration.mobile_number')}
                  placeholder={t(
                    'provider_registration.mobile_number_placeholder'
                  )}
                  type="tel"
                />
              </div>

              <div className="">
                <Autocomplete
                  radius="sm"
                  isRequired
                  labelPlacement="outside"
                  size="md"
                  variant="bordered"
                  label={t('provider_registration.gender')}
                  placeholder={t('provider_registration.gender_placeholder')}
                >
                  {gender.map((animal) => (
                    <AutocompleteItem key={animal.key}>
                      {animal.label}
                    </AutocompleteItem>
                  ))}
                </Autocomplete>
              </div>

              <div>
                <DatePicker
                  radius="sm"
                  variant="bordered"
                  size="md"
                  showMonthAndYearPickers
                  label={t('provider_registration.select_date')}
                  isRequired
                  labelPlacement="outside"
                />
              </div>
            </div>

            {/* Bio (Textarea) */}
            <div className="mt-4">
              <Textarea
                label={t('provider_registration.bio')}
                isRequired
                isClearable
                labelPlacement="outside"
                placeholder={t('provider_registration.bio_placeholder')}
                variant="bordered"
                onClear={() => console.log('Bio cleared')}
              />
            </div>
          </div>

          {/* ADDRESS INFORMATION */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-gray-700 mb-10">
              {t('provider_registration.address_information')}
            </h3>
            <div className="mt-12 mb-6">
              <CustomInput
                label={t('provider_registration.address')}
                isRequired
                radius="sm"
                variant="bordered"
                size="md"
                placeholder={t('provider_registration.address_placeholder')}
                type="text"
              />
            </div>
            <div className="grid grid-cols-4 gap-5 mt-2">
              <div>
                <CustomInput
                  isRequired
                  radius="sm"
                  variant="bordered"
                  size="md"
                  label={t('provider_registration.country')}
                  placeholder={t('provider_registration.country_placeholder')}
                  type="tel"
                />
              </div>

              <div>
                <CustomInput
                  isRequired
                  radius="sm"
                  variant="bordered"
                  size="md"
                  label={t('provider_registration.state')}
                  placeholder={t('provider_registration.state_placeholder')}
                  type="tel"
                />
              </div>

              <div>
                <CustomInput
                  isRequired
                  radius="sm"
                  variant="bordered"
                  size="md"
                  label={t('provider_registration.city')}
                  placeholder={t('provider_registration.city_placeholder')}
                  type="tel"
                />
              </div>

              <div>
                <CustomInput
                  isRequired
                  radius="sm"
                  variant="bordered"
                  size="md"
                  label={t('provider_registration.postal_code')}
                  placeholder={t(
                    'provider_registration.postal_code_placeholder'
                  )}
                  type="tel"
                />
              </div>
            </div>

            <div className="grid grid-cols-4 gap-5 mt-6">
              <div className="">
                <Autocomplete
                  radius="sm"
                  isRequired
                  labelPlacement="outside"
                  size="md"
                  variant="bordered"
                  label={t('provider_registration.language')}
                  placeholder={t('provider_registration.language_placeholder')}
                >
                  {language.map((lng) => (
                    <AutocompleteItem key={lng.key}>
                      {lng.label}
                    </AutocompleteItem>
                  ))}
                </Autocomplete>
              </div>
              <div className="">
                <Autocomplete
                  radius="sm"
                  isRequired
                  labelPlacement="outside"
                  size="md"
                  variant="bordered"
                  label={t('provider_registration.currency')}
                  placeholder={t('provider_registration.currency_placeholder')}
                >
                  {currency.map((cur) => (
                    <AutocompleteItem key={cur.key}>
                      {cur.label}
                    </AutocompleteItem>
                  ))}
                </Autocomplete>
              </div>
            </div>
          </div>
          <div className="flex justify-end items-center mt-10 gap-5">
            <CustomButton
              size="md"
              label={t('provider_registration.clear')}
              color="danger"
            />
            <CustomButton
              size="md"
              label={t('provider_registration.next')}
              color="primary"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ProviderRegistration;
